import PDFMake from 'pdfmake'
import pdfFonts from 'pdfmake/build/vfs_fonts'
import { getFooter, nestedTableStyling, formatAddress } from '@/utils/pdf-formatters/GenericSettlementsFunctions.js'
import store from '@/store/index.js'
import { sumsFor } from '@/utils/base.js'
import i18n from '@/i18n'
import { chunk } from 'lodash'
import { FileNames } from '@/utils/Files'

PDFMake.vfs = pdfFonts.pdfMake.vfs

const SIDE_MARGIN = 30
const noBorder = [false, false, false, false]
const bottomBorder = [false, false, false, true]
const topBorder = [false, true, false, false]

export function generateTractGroupAnalysisReport ({ tractGroup, items, headers, sumHeaders, isSpeciesView }) {
  const columns = headers.filter(c => !!c.text)
  const table = getAnalysisTable(items, columns, sumHeaders)

  const docDefinition = {
    header: getHeader(tractGroup, isSpeciesView),
    footer: getFooter,
    pageMargins: [SIDE_MARGIN, 100, SIDE_MARGIN, 30],
    pageOrientation: 'landscape',
    headerRows: 1,
    styles: {
      header: { fontSize: 100 },
      rightAlign: { alignment: 'right' },
      nestedTable: { fontSize: 8 }
    },
    content: [...table],
    style: 'nestedTable'
  }

  PDFMake.createPdf(docDefinition).download(FileNames.tractGroupAnalysisPdf({ tractGroup: tractGroup?.name }))
}

function getAnalysisTable (items, columns, sumHeaders) {
  const headerFontSize = 10
  const bodyFontSize = 8
  const result = []

  if (columns.length === 0) return result

  const firstColumn = columns[0]
  const restColumns = columns.slice(1)
  const columnsChunked = restColumns.length > 0 ? chunk(restColumns, 12) : [[]]

  columnsChunked.forEach(chunkCols => {
    const tableColumns = [firstColumn, ...chunkCols]

    const headerRow = tableColumns.map(c => ({
      text: c.text,
      bold: true,
      border: bottomBorder,
      fontSize: headerFontSize,
      alignment: c.align
    }))

    const bodyRows = items.map((item, idx) => tableColumns.map(c => ({
      text: c.format ? c.format(item[c.value]) : item[c.value],
      border: noBorder,
      fontSize: bodyFontSize,
      alignment: c.align,
      fillColor: idx % 2 === 0 ? '#EEEEEE' : null
    })))

    const totalsValues = sumsFor(items, sumHeaders)
    const totalsRow = tableColumns.map((c, idx) => {
      if (idx === 0) {
        return {
          text: i18n.t('totals'),
          bold: true,
          fontSize: bodyFontSize,
          alignment: c.align,
          border: topBorder
        }
      }
      const hasTotal = c.total !== undefined && sumHeaders.includes(c.value)
      const value = hasTotal
        ? c.total({ items, total: totalsValues[c.value], sums: totalsValues, count: items.length })
        : ' '
      return {
        text: hasTotal && c.format ? c.format(value) : value,
        bold: true,
        fontSize: bodyFontSize,
        alignment: c.align,
        border: topBorder
      }
    })

    const body = [headerRow, ...bodyRows, totalsRow]

    result.push({
      marginBottom: 20,
      table: {
        widths: tableColumns.map(() => '*'),
        body,
        dontBreakRows: true,
        headerRows: 1,
        keepWithHeaderRows: true
      },
      layout: {
        hLineWidth: (i, node) => (i === 0 || i === node.table.body.length) ? 2 : 1,
        vLineWidth: (i, node) => (i === 0 || i === node.table.widths.length) ? 2 : 1,
        hLineColor: i => i < 2 || i > 4 ? 'black' : 'white',
        vLineColor: () => 'black'
      },
      style: nestedTableStyling
    })
  })

  return result
}

function getHeader (tractGroup, isSpeciesView) {
  const companyInfo = store.getters['user/companyInfo']
  const companyName = companyInfo.name ? companyInfo.name : ''
  const companyAddress = formatAddress(companyInfo)
  const companyPhoneNumber = companyInfo.mainContact?.phoneNumber || ''

  const subtitleParts = [
    tractGroup?.tractTypeName,
    isSpeciesView ? i18n.t('species') : i18n.t('product')
  ].filter(Boolean).join(' • ')

  return {
    fontSize: 15,
    bold: true,
    marginTop: 20,
    marginLeft: 35,
    marginRight: 35,
    table: {
      widths: '*',
      body: [
        [
          {
            table: {
              body: [
                [''],
                [{ text: companyName, alignment: 'left' }],
                [{ text: companyAddress, alignment: 'left', fontSize: 8 }],
                [{ text: companyPhoneNumber, alignment: 'left', fontSize: 8 }]
              ]
            },
            layout: 'noBorders'
          },
          {
            alignment: 'center',
            table: {
              widths: '*',
              body: [
                [''],
                [{ text: `${tractGroup?.name ?? ''} ${i18n.t('analysis')}`, fontSize: 15, alignment: 'center' }],
                [{ text: subtitleParts, fontSize: 9, alignment: 'center', bold: false }]
              ]
            },
            layout: 'noBorders'
          },
          { text: '' }
        ]
      ]
    },
    layout: 'noBorders'
  }
}
